import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { FileEdit } from 'lucide-react';
import type { TransactionsTabProps } from './types';
import { NominalPicker } from '@/components/assets/detail/NominalPicker';
import { CostCentrePicker } from '@/components/assets/detail/CostCentrePicker';

export function TransactionsTab({
  options,
  onOptionsChange,
  setHasUnsavedChanges,
  nominalAccounts,
  costCentres,
  loadingCostCentres,
  loadingNominalAccounts,
}: Readonly<TransactionsTabProps>): React.ReactElement {
  return (
    <div className="space-y-6">
      {/* Nominal Accounts Card */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileEdit className="h-5 w-5 text-primary" />
            Nominal Accounts
          </CardTitle>
          <CardDescription>
            Configure nominal accounts for asset transactions. Accounts are fetched from the integration database if linked.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="assetCost">Asset Cost</Label>
              <NominalPicker
                value={options.transactions.nominalAccounts.assetCost ?? ''}
                onValueChange={(code) => {
                  const newAccounts = { ...options.transactions.nominalAccounts, assetCost: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                  }));
                  setHasUnsavedChanges(true);
                }}
                nominalAccounts={nominalAccounts}
                loading={loadingNominalAccounts}
                placeholder="Select nominal account..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="depreciationBS">Depreciation B/S</Label>
              <NominalPicker
                value={options.transactions.nominalAccounts.depreciationBS ?? ''}
                onValueChange={(code) => {
                  const newAccounts = { ...options.transactions.nominalAccounts, depreciationBS: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                  }));
                  setHasUnsavedChanges(true);
                }}
                nominalAccounts={nominalAccounts}
                loading={loadingNominalAccounts}
                placeholder="Select nominal account..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="depreciationPL">Depreciation P&L</Label>
              <NominalPicker
                value={options.transactions.nominalAccounts.depreciationPL ?? ''}
                onValueChange={(code) => {
                  const newAccounts = { ...options.transactions.nominalAccounts, depreciationPL: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                  }));
                  setHasUnsavedChanges(true);
                }}
                nominalAccounts={nominalAccounts}
                loading={loadingNominalAccounts}
                placeholder="Select nominal account..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="disposal">Disposal</Label>
              <NominalPicker
                value={options.transactions.nominalAccounts.disposal ?? ''}
                onValueChange={(code) => {
                  const newAccounts = { ...options.transactions.nominalAccounts, disposal: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                  }));
                  setHasUnsavedChanges(true);
                }}
                nominalAccounts={nominalAccounts}
                loading={loadingNominalAccounts}
                placeholder="Select nominal account..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="revaluationBS">Revaluation B/S</Label>
              <NominalPicker
                value={options.transactions.nominalAccounts.revaluationBS ?? ''}
                onValueChange={(code) => {
                  const newAccounts = { ...options.transactions.nominalAccounts, revaluationBS: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                  }));
                  setHasUnsavedChanges(true);
                }}
                nominalAccounts={nominalAccounts}
                loading={loadingNominalAccounts}
                placeholder="Select nominal account..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="revaluationPL">Revaluation P&L</Label>
              <NominalPicker
                value={options.transactions.nominalAccounts.revaluationPL ?? ''}
                onValueChange={(code) => {
                  const newAccounts = { ...options.transactions.nominalAccounts, revaluationPL: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                  }));
                  setHasUnsavedChanges(true);
                }}
                nominalAccounts={nominalAccounts}
                loading={loadingNominalAccounts}
                placeholder="Select nominal account..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="disposalsBankAccount">Disposals Bank Account</Label>
              <NominalPicker
                value={options.transactions.nominalAccounts.disposalsBankAccount ?? ''}
                onValueChange={(code) => {
                  const newAccounts = { ...options.transactions.nominalAccounts, disposalsBankAccount: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                  }));
                  setHasUnsavedChanges(true);
                }}
                nominalAccounts={nominalAccounts}
                loading={loadingNominalAccounts}
                placeholder="Select nominal account..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="additionsWIPBankAcc">Additions WIP / Bank Acc</Label>
              <NominalPicker
                value={options.transactions.nominalAccounts.additionsWIPBankAcc ?? ''}
                onValueChange={(code) => {
                  const newAccounts = { ...options.transactions.nominalAccounts, additionsWIPBankAcc: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, nominalAccounts: newAccounts }
                  }));
                  setHasUnsavedChanges(true);
                }}
                nominalAccounts={nominalAccounts}
                loading={loadingNominalAccounts}
                placeholder="Select nominal account..."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Costing Card */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle>Costing</CardTitle>
          <CardDescription>
            Configure cost centre codes for asset transactions. These codes will be used when posting Nominal Journals if no Cost Centre is set on the Asset. The Project Code used is always set on the Asset. These Cost Centres must exist in every Project.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="costingDepreciation">Depreciation</Label>
              <CostCentrePicker
                value={options.transactions.costing.depreciation ?? ''}
                onValueChange={(code) => {
                  const newCosting = { ...options.transactions.costing, depreciation: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, costing: newCosting }
                  }));
                  setHasUnsavedChanges(true);
                }}
                costCentres={costCentres}
                loading={loadingCostCentres}
                placeholder="Select cost centre..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="costingAddition">Addition</Label>
              <CostCentrePicker
                value={options.transactions.costing.addition ?? ''}
                onValueChange={(code) => {
                  const newCosting = { ...options.transactions.costing, addition: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, costing: newCosting }
                  }));
                  setHasUnsavedChanges(true);
                }}
                costCentres={costCentres}
                loading={loadingCostCentres}
                placeholder="Select cost centre..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="costingDisposals">Disposals</Label>
              <CostCentrePicker
                value={options.transactions.costing.disposals ?? ''}
                onValueChange={(code) => {
                  const newCosting = { ...options.transactions.costing, disposals: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, costing: newCosting }
                  }));
                  setHasUnsavedChanges(true);
                }}
                costCentres={costCentres}
                loading={loadingCostCentres}
                placeholder="Select cost centre..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="costingRevaluation">Revaluation</Label>
              <CostCentrePicker
                value={options.transactions.costing.revaluation ?? ''}
                onValueChange={(code) => {
                  const newCosting = { ...options.transactions.costing, revaluation: code };
                  onOptionsChange(prev => ({
                    ...prev,
                    transactions: { ...prev.transactions, costing: newCosting }
                  }));
                  setHasUnsavedChanges(true);
                }}
                costCentres={costCentres}
                loading={loadingCostCentres}
                placeholder="Select cost centre..."
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Post Costing Transactions Card */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle>Post Costing Transactions</CardTitle>
          <CardDescription>
            Configure which transaction types should post to costing and whether to post credit entries
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="mb-4 font-medium">Post Costing Transactions</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center space-x-2 rounded-lg border p-3">
                <Checkbox
                  id="postCostingDepreciation"
                  checked={options.transactions.postCostingTransactions.depreciation}
                  onCheckedChange={(checked) => {
                    const newPostCosting = { ...options.transactions.postCostingTransactions, depreciation: checked as boolean };
                    onOptionsChange(prev => ({
                      ...prev,
                      transactions: { ...prev.transactions, postCostingTransactions: newPostCosting }
                    }));
                    setHasUnsavedChanges(true);
                  }}
                />
                <Label htmlFor="postCostingDepreciation" className="cursor-pointer font-normal">
                  Post Costing Transactions for Depreciation
                </Label>
              </div>

              <div className="flex items-center space-x-2 rounded-lg border p-3">
                <Checkbox
                  id="postCostingAdditions"
                  checked={options.transactions.postCostingTransactions.additions}
                  onCheckedChange={(checked) => {
                    const newPostCosting = { ...options.transactions.postCostingTransactions, additions: checked as boolean };
                    onOptionsChange(prev => ({
                      ...prev,
                      transactions: { ...prev.transactions, postCostingTransactions: newPostCosting }
                    }));
                    setHasUnsavedChanges(true);
                  }}
                />
                <Label htmlFor="postCostingAdditions" className="cursor-pointer font-normal">
                  Post Costing Transactions for Additions
                </Label>
              </div>

              <div className="flex items-center space-x-2 rounded-lg border p-3">
                <Checkbox
                  id="postCostingRevaluations"
                  checked={options.transactions.postCostingTransactions.revaluations}
                  onCheckedChange={(checked) => {
                    const newPostCosting = { ...options.transactions.postCostingTransactions, revaluations: checked as boolean };
                    onOptionsChange(prev => ({
                      ...prev,
                      transactions: { ...prev.transactions, postCostingTransactions: newPostCosting }
                    }));
                    setHasUnsavedChanges(true);
                  }}
                />
                <Label htmlFor="postCostingRevaluations" className="cursor-pointer font-normal">
                  Post Costing Transactions for Revaluations
                </Label>
              </div>

              <div className="flex items-center space-x-2 rounded-lg border p-3">
                <Checkbox
                  id="postCostingDisposals"
                  checked={options.transactions.postCostingTransactions.disposals}
                  onCheckedChange={(checked) => {
                    const newPostCosting = { ...options.transactions.postCostingTransactions, disposals: checked as boolean };
                    onOptionsChange(prev => ({
                      ...prev,
                      transactions: { ...prev.transactions, postCostingTransactions: newPostCosting }
                    }));
                    setHasUnsavedChanges(true);
                  }}
                />
                <Label htmlFor="postCostingDisposals" className="cursor-pointer font-normal">
                  Post Costing Transactions for Disposals
                </Label>
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <h4 className="mb-4 font-medium">Post CREDIT to Costing</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex items-center space-x-2 rounded-lg border p-3">
                <Checkbox
                  id="postCreditDepreciation"
                  checked={options.transactions.postCreditToCosting.depreciation}
                  onCheckedChange={(checked) => {
                    const newPostCredit = { ...options.transactions.postCreditToCosting, depreciation: checked as boolean };
                    onOptionsChange(prev => ({
                      ...prev,
                      transactions: { ...prev.transactions, postCreditToCosting: newPostCredit }
                    }));
                    setHasUnsavedChanges(true);
                  }}
                />
                <Label htmlFor="postCreditDepreciation" className="cursor-pointer font-normal">
                  Post CREDIT Depreciation to Costing
                </Label>
              </div>

              <div className="flex items-center space-x-2 rounded-lg border p-3">
                <Checkbox
                  id="postCreditAdditions"
                  checked={options.transactions.postCreditToCosting.additions}
                  onCheckedChange={(checked) => {
                    const newPostCredit = { ...options.transactions.postCreditToCosting, additions: checked as boolean };
                    onOptionsChange(prev => ({
                      ...prev,
                      transactions: { ...prev.transactions, postCreditToCosting: newPostCredit }
                    }));
                    setHasUnsavedChanges(true);
                  }}
                />
                <Label htmlFor="postCreditAdditions" className="cursor-pointer font-normal">
                  Post CREDIT Additions to Costing
                </Label>
              </div>

              <div className="flex items-center space-x-2 rounded-lg border p-3">
                <Checkbox
                  id="postCreditRevaluations"
                  checked={options.transactions.postCreditToCosting.revaluations}
                  onCheckedChange={(checked) => {
                    const newPostCredit = { ...options.transactions.postCreditToCosting, revaluations: checked as boolean };
                    onOptionsChange(prev => ({
                      ...prev,
                      transactions: { ...prev.transactions, postCreditToCosting: newPostCredit }
                    }));
                    setHasUnsavedChanges(true);
                  }}
                />
                <Label htmlFor="postCreditRevaluations" className="cursor-pointer font-normal">
                  Post CREDIT Revaluations to Costing
                </Label>
              </div>

              <div className="flex items-center space-x-2 rounded-lg border p-3">
                <Checkbox
                  id="postCreditDisposals"
                  checked={options.transactions.postCreditToCosting.disposals}
                  onCheckedChange={(checked) => {
                    const newPostCredit = { ...options.transactions.postCreditToCosting, disposals: checked as boolean };
                    onOptionsChange(prev => ({
                      ...prev,
                      transactions: { ...prev.transactions, postCreditToCosting: newPostCredit }
                    }));
                    setHasUnsavedChanges(true);
                  }}
                />
                <Label htmlFor="postCreditDisposals" className="cursor-pointer font-normal">
                  Post CREDIT Disposals to Costing
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
